// src/utils/apiClient.js
// Wraps fetch to attach the current Clerk session token as a Bearer auth
// header. Required for any /api endpoint that verifies the request's
// userId against the caller's session server-side (see api/_lib/verifyAuth.js).
//
// Two ways to use this:
// 1. authedFetch(getToken, url, options) — pass a Clerk getToken function
//    directly. Used by components/contexts that already call useAuth()
//    (e.g. MyVendorsContext.js, MyVendorsPage.jsx).
// 2. apiFetch(url, options) — uses a globally registered getToken, set once
//    by <AuthTokenBridge /> near the app root. Use this in modules that
//    aren't React components and can't call useAuth() themselves (e.g.
//    src/utils/alertsApi.js, src/utils/supabase.js), or in components where
//    wiring useAuth() through just for this would be unnecessary overhead.

let _globalGetToken = null;

export function registerTokenGetter(getToken) {
  _globalGetToken = getToken;
}

export async function authedFetch(getToken, url, options = {}) {
  const token = await getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

export async function apiFetch(url, options = {}) {
  // Guard against the rare case a request fires before AuthTokenBridge's
  // first effect has run (e.g. a component mounted above it in the tree).
  // Polls briefly rather than failing the request outright.
  let waited = 0;
  while (!_globalGetToken && waited < 2000) {
    await new Promise((r) => setTimeout(r, 50));
    waited += 50;
  }
  const token = _globalGetToken ? await _globalGetToken() : null;
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}